import type * as React from "react";
import { useEffect, useId, useRef, useState } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface ComboboxProps {
  /** Current input text. */
  value: string;
  onValueChange: (value: string) => void;
  /** Fired when an option is deliberately chosen (Enter or click). */
  onSelect?: (value: string) => void;
  /** Already filtered by the caller — the two consumers filter differently. */
  options: string[];
  /** Accessible name for the text input. */
  label: string;
  /** Render the label visually as well as for assistive tech. */
  showLabel?: boolean;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** Extra key handling, e.g. Backspace removing the last tag. Runs after the
   *  combobox has handled navigation keys and only if it did not consume them. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Rendered immediately before the input, inside the control group. */
  prefix?: React.ReactNode;
  /** Rendered after the input, e.g. a reset button. */
  children?: React.ReactNode;
  /**
   * Fires whenever the listbox opens or closes.
   *
   * A dialog around this needs it: Radix dismissable layers listen for Escape
   * at the document in the *capture* phase, so an Escape meant for the listbox
   * reaches the dialog first and closes it. The only way to hold that back is
   * `onEscapeKeyDown` + `preventDefault()` on the content, which means the
   * caller has to know whether the listbox is showing.
   */
  onOpenChange?: (open: boolean) => void;
}

/**
 * An ARIA 1.2 combobox with an inline listbox.
 *
 * Hand-wired rather than built on `@radix-ui/react-popover`: Popover moves focus
 * into the popover and traps it, whereas a combobox must keep DOM focus in the
 * input and drive selection with `aria-activedescendant`. Fighting that would
 * still leave every ARIA attribute and key handler to write by hand.
 */
export function Combobox({
  value,
  onValueChange,
  onSelect,
  options,
  label,
  showLabel,
  placeholder,
  className,
  inputClassName,
  onKeyDown,
  prefix,
  children,
  onOpenChange,
}: ComboboxProps) {
  const id = useId();
  const inputId = `${id}-input`;
  const listboxId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const isOpen = open && options.length > 0;

  // Keep the active option in range as the caller re-filters.
  useEffect(() => {
    setActiveIndex((i) => (i >= options.length ? -1 : i));
  }, [options.length]);

  // Through a ref, so the effect keys off the open state alone: a caller
  // passing an inline callback would otherwise re-notify on every render, and
  // one that set state from it could spin.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  // Dismiss on outside pointer down rather than a blur timeout: the old 100ms
  // and 150ms timers raced, and left the list hovering over the next field.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isOpen]);

  // ...and on focus landing anywhere else, or tabbing to the next field would
  // leave the list hovering over it. A document-level `focusin` for the same
  // reason the dismissal above is one: React's synthetic blur is delivered
  // through the React tree, which this component cannot rely on from inside a
  // portal, and `relatedTarget` is not dependable across browsers. This sees
  // every way focus can move — Tab, Shift+Tab, a click, a programmatic focus.
  useEffect(() => {
    if (!isOpen) return;
    const onFocusIn = (e: FocusEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setActiveIndex(-1);
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [isOpen]);

  const commit = (option: string) => {
    onValueChange(option);
    onSelect?.(option);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && isOpen) {
      e.preventDefault();
      setActiveIndex((i) => (i < options.length - 1 ? i + 1 : 0));
      return;
    }
    if (e.key === "ArrowUp" && isOpen) {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : options.length - 1));
      return;
    }
    if (e.key === "ArrowDown" && !open && options.length > 0) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(0);
      return;
    }
    if (e.key === "Home" && isOpen) {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (e.key === "End" && isOpen) {
      e.preventDefault();
      setActiveIndex(options.length - 1);
      return;
    }
    if (e.key === "Enter" && isOpen && activeIndex >= 0) {
      e.preventDefault();
      commit(options[activeIndex]);
      return;
    }
    // First Escape closes the list without clearing what has been typed.
    if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    onKeyDown?.(e);
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <label
        htmlFor={inputId}
        className={cn("text-sm font-medium", !showLabel && "sr-only")}
      >
        {label}
      </label>
      <div className="flex flex-row gap-2">
        {prefix}
        <Input
          id={inputId}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          value={value}
          placeholder={placeholder}
          className={inputClassName}
          onChange={(e) => {
            onValueChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {children}
      </div>

      {/* Always rendered so assistive tech can resolve aria-controls. */}
      <ul
        id={listboxId}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: ul with role="listbox" is the markup the ARIA combobox pattern prescribes
        role="listbox"
        aria-label={label}
        hidden={!isOpen}
        className="absolute z-50 top-full left-0 right-0 mt-1 flex flex-col max-h-[20em] overflow-y-auto rounded-md border border-border-strong bg-popover text-popover-foreground p-1 shadow-md"
      >
        {options.map((option, index) => (
          // biome-ignore lint/a11y/useFocusableInteractive: options must NOT be focusable — the input keeps focus and aria-activedescendant conveys the active option
          // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection is handled on the input (Enter commits the active option), not per option
          <li
            key={option}
            id={optionId(index)}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: li with role="option" is prescribed by the ARIA combobox pattern
            role="option"
            aria-selected={index === activeIndex}
            // Focus never leaves the input; aria-activedescendant conveys the
            // highlight. preventDefault keeps the input from blurring on click.
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => commit(option)}
            onMouseEnter={() => setActiveIndex(index)}
            className={cn(
              "cursor-pointer rounded-sm px-2 py-1.5 text-sm",
              index === activeIndex && "bg-accent text-accent-foreground",
            )}
          >
            {option}
          </li>
        ))}
      </ul>

      {/* Scoped to this combobox so it cannot contend with the app announcer. */}
      <div aria-live="polite" className="sr-only">
        {isOpen ? `${options.length} suggestions available` : ""}
      </div>
    </div>
  );
}
